import { mods } from '@approvalmax/theme';
import { ActionMenu, NumberEditor, TextEditor, TransparentButton } from '@approvalmax/ui';
import { Button, Flex } from '@approvalmax/ui/src/components';
import { intl, mathService } from '@approvalmax/utils';
import { StyledMods } from '@styled-kit/mods';
import { constants, selectors } from 'modules/common';
import AttachmentsDropdown from 'modules/components/Attachment/AttachmentsDropdown';
import { backend, domain } from 'modules/data';
import { AmountDueIndicator, BankAccountCheckIndicator } from 'modules/request';
import { TbEllipsisColorlessIcon } from 'modules/sprites';
import moment from 'moment';
import { RequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { ColumnDefinition } from 'pages/requestList/selectors/types/ColumnDefinition';
import Skeleton from 'react-loading-skeleton';
import { GetBatchPaymentsRequestAmounts } from 'shared/data/webApp/v2';
import { calculateBillAmountDue } from 'shared/helpers';
import styled from 'styled-components';

import { messages } from './tableConfig.messages';

const { xeroConstants } = constants;

const TextBlock = styled.div`
    max-width: 300px;
    white-space: break-spaces;
`;

const ActionButton = styled(TransparentButton)`
    padding: 8px 4px;
`;

const StyledNumberEditor = styled(NumberEditor)`
    max-width: 90px;
`;

const StyledTextEditor = styled(TextEditor)`
    max-width: 90px;
`;

const BankAccountEditor = styled(TextEditor)<StyledMods<{ hasWarning: boolean }>>`
    ${mods('hasWarning', true)`
        border-color: #a3341a;
    `}
`;

export const StyledActionMenu = styled(ActionMenu)`
    opacity: 0;
    transition: opacity 0.3s ease-in;
    pointer-events: none;

    &:focus-within {
        opacity: 1;
        pointer-events: auto;
    }
`;

interface ColumnOptions {
    removeSelectableBillById: (lineId: string) => void;
    countryCode: backend.OrganisationVersion;
    changeBillInvoiceItemField: (
        xeroBillInvoiceRequestId: string,
        itemField: keyof domain.PaymentItem,
        newValue: domain.PaymentItem[keyof domain.PaymentItem]
    ) => void;
    showErrors: boolean;
    billInvoiceRequestIdsWithError: string[];
    billInvoiceRequestIdsWithExceededAmount: string[];
    onSelectAttachment: (
        attachmentId: string,
        xeroBatchPaymentInvoiceId: string,
        xeroBatchPaymentInvoiceName: string
    ) => void;
    onSelectBill: (xeroBatchPaymentInvoiceId: string) => void;
    activeAttachmentId?: string;
    currency: string;
    workflowIsSupplierBankAccountRequired?: boolean;
    workflowAllowEditSupplierBankAccount?: boolean;
    companyId: string;
    editMode: RequestEditMode;
    removeLinesDisabled: boolean;
    amounts: GetBatchPaymentsRequestAmounts['response']['items'] | undefined;
    isAmountsLoading: boolean;
}

export const getColumnDefinitions = ({
    removeSelectableBillById,
    countryCode,
    changeBillInvoiceItemField,
    showErrors,
    billInvoiceRequestIdsWithError,
    billInvoiceRequestIdsWithExceededAmount,
    onSelectBill,
    onSelectAttachment,
    activeAttachmentId,
    currency,
    workflowIsSupplierBankAccountRequired,
    workflowAllowEditSupplierBankAccount,
    editMode,
    removeLinesDisabled,
    amounts,
    isAmountsLoading,
}: ColumnOptions): ColumnDefinition<domain.PaymentItem>[] => {
    const columns: ColumnDefinition<domain.PaymentItem>[] = [
        {
            id: 'item',
            name: messages.itemName,
            minWidth: 150,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.friendlyName,
            cell: (value, li) => (
                <Button
                    link
                    size='small'
                    onClick={() => onSelectBill(li.xeroBillInvoiceRequestId)}
                    variant='text'
                    color='blue100'
                >
                    {value}
                </Button>
            ),
        },
        {
            id: 'supplier',
            name: messages.supplierName,
            minWidth: 100,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.contact.text,
            cell: (value, li) => {
                // hide until backend side will work correctly
                /*
                const outstandingBalanceText =
                    li.outstandingBalance !== null
                        ? messages.outstandingBalance({
                              amount: intl.formatCurrency(li.outstandingBalance, li.currency),
                          })
                        : '';

                const overdueBalanceText =
                    li.overdueBalance !== null
                        ? messages.overdueBalance({
                              amount: intl.formatCurrency(li.overdueBalance, li.currency),
                          })
                        : '';

                const hoverTitle = [li.contact.text, outstandingBalanceText, overdueBalanceText]
                    .filter((line) => line)
                    .join('\n');
                */

                const hoverTitle = li.contact.text;

                return <TextBlock title={hoverTitle}>{value}</TextBlock>;
            },
        },
        {
            id: 'billsReference',
            name: messages.billsReferenceName,
            alwaysVisible: true,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.xeroBillInvoiceReference || '—',
            cell: (value) => <TextBlock>{value}</TextBlock>,
        },
        {
            id: 'date',
            name: messages.dateName,
            minWidth: 80,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => moment.utc(li.date).format('ll'),
        },
        {
            id: 'dueDate',
            name: messages.dueDateName,
            minWidth: 80,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => moment.utc(li.dueDate).format('ll'),
        },
        {
            id: 'amountDue',
            name: messages.amountDueName,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => {
                const amountDue = calculateBillAmountDue(li, amounts, true);

                return intl.formatNumber(amountDue);
            },
            alignRight: true,
            hideTitle: true,
            cell: (value: string, li) => {
                if (isAmountsLoading) {
                    return <Skeleton />;
                }

                const billAmount = amounts?.find((amount) => amount.invoiceRequestId === li.xeroBillInvoiceRequestId);

                return (
                    <AmountDueIndicator
                        currency={li.currency}
                        amountOnApproval={billAmount?.amountOnApproval ?? li.amountOnApproval}
                        amountInProcessing={billAmount?.amountInProcessing ?? li.amountInProcessing}
                        amountAwaitingPayment={billAmount?.amountAwaitingPayment ?? li.amountAwaitingPayment}
                        amountOnReview={billAmount?.amountOnReview ?? li.amountOnReview}
                    >
                        {value}
                    </AmountDueIndicator>
                );
            },
        },
        {
            id: 'payment',
            name: messages.payment({ currency }),
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => String(li.amount),
            alwaysVisible: true,
            alignRight: true,
            required: true,
            cell: (value: string, li, disabled) => (
                <StyledNumberEditor
                    value={li.amount}
                    min={0}
                    align='right'
                    invalid={
                        showErrors &&
                        (!li.amount || billInvoiceRequestIdsWithExceededAmount.includes(li.xeroBillInvoiceRequestId))
                    }
                    precision={2}
                    disabled={disabled || editMode === RequestEditMode.Approver}
                    max={mathService.subtract(
                        li.amountDue,
                        mathService.add(
                            mathService.add(li.amountOnApproval ?? 0, li.amountInProcessing ?? 0),
                            li.amountAwaitingPayment ?? 0
                        )
                    )}
                    onChange={(val: number) => changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'amount', val)}
                    changeOnBlur={false}
                />
            ),
        },
    ];

    if (selectors.field.getSupportRegionalFieldByCountryCode(countryCode, domain.RegionalField.Details)) {
        columns.push({
            id: 'details',
            name: messages.details,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.details || '',
            alwaysVisible: true,
            cell: (value: string, li, disabled) => (
                <TextEditor
                    value={value}
                    maxLength={xeroConstants.BATCH_PAYMENT_DETAILS_MAX_LENGTH}
                    disabled={disabled || editMode === RequestEditMode.Approver}
                    onChange={(val: string) => changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'details', val)}
                    invalid={
                        showErrors && !!li.details && li.details.length > xeroConstants.BATCH_PAYMENT_DETAILS_MAX_LENGTH
                    }
                />
            ),
        });
    }

    if (selectors.field.getSupportRegionalFieldByCountryCode(countryCode, domain.RegionalField.Particulars)) {
        columns.push({
            id: 'particulars',
            name: messages.particulars,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.particulars || '',
            alwaysVisible: true,
            cell: (value: string, li, disabled) => (
                <StyledTextEditor
                    value={value}
                    maxLength={xeroConstants.BATCH_PAYMENT_PARTICULARS_MAX_LENGTH}
                    disabled={disabled || editMode === RequestEditMode.Approver}
                    onChange={(val: string) =>
                        changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'particulars', val)
                    }
                    invalid={
                        showErrors &&
                        !!li.particulars &&
                        li.particulars.length > xeroConstants.BATCH_PAYMENT_PARTICULARS_MAX_LENGTH
                    }
                />
            ),
        });
    }

    if (selectors.field.getSupportRegionalFieldByCountryCode(countryCode, domain.RegionalField.Code)) {
        columns.push({
            id: 'code',
            name: messages.code,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.code || '',
            alwaysVisible: true,
            cell: (value: string, li, disabled) => (
                <StyledTextEditor
                    value={value}
                    maxLength={xeroConstants.BATCH_PAYMENT_CODE_MAX_LENGTH}
                    disabled={disabled || editMode === RequestEditMode.Approver}
                    onChange={(val: string) => changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'code', val)}
                    invalid={showErrors && !!li.code && li.code.length > xeroConstants.BATCH_PAYMENT_CODE_MAX_LENGTH}
                />
            ),
        });
    }

    if (selectors.field.getSupportRegionalFieldByCountryCode(countryCode, domain.RegionalField.Reference)) {
        const referenceFieldMaxLength =
            countryCode === backend.OrganisationVersion.AU
                ? xeroConstants.BATCH_PAYMENT_REFERENCE_AU_MAX_LENGTH
                : xeroConstants.BATCH_PAYMENT_REFERENCE_DEFAULT_MAX_LENGTH;

        columns.push({
            id: 'reference',
            name: messages.reference,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.reference || '',
            alwaysVisible: true,
            cell: (value: string, li, disabled) => (
                <StyledTextEditor
                    value={value}
                    maxLength={referenceFieldMaxLength}
                    disabled={disabled || editMode === RequestEditMode.Approver}
                    onChange={(val: string) =>
                        changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'reference', val)
                    }
                    invalid={showErrors && !!li.reference && li.reference.length > referenceFieldMaxLength}
                />
            ),
        });
    }

    columns.push(
        {
            id: 'bankAccount',
            name: messages.bankAccountName,
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.bankAccount || '',
            required: workflowIsSupplierBankAccountRequired,
            alwaysVisible: true,
            cell: (value: string, li, disabled) => (
                <BankAccountCheckIndicator
                    actualBankAccount={li.bankAccount || '—'}
                    expectedBankAccount={li.defaultBankAccount || '—'}
                >
                    {(hasWarning) => (
                        <BankAccountEditor
                            $hasWarning={hasWarning}
                            value={value}
                            maxLength={xeroConstants.BATCH_PAYMENT_BANK_ACCOUNT_MAX_LENGTH}
                            disabled={
                                disabled ||
                                !workflowAllowEditSupplierBankAccount ||
                                editMode === RequestEditMode.Approver
                            }
                            onChange={(val: string) =>
                                changeBillInvoiceItemField(li.xeroBillInvoiceRequestId, 'bankAccount', val)
                            }
                            invalid={
                                showErrors &&
                                ((!!li.bankAccount &&
                                    li.bankAccount.length > xeroConstants.BATCH_PAYMENT_BANK_ACCOUNT_MAX_LENGTH) ||
                                    (workflowIsSupplierBankAccountRequired && !li.bankAccount))
                            }
                        />
                    )}
                </BankAccountCheckIndicator>
            ),
        },
        {
            id: 'attachments',
            name: '',
            value: (li) => String(li.attachments.length),
            cell: (val, li) =>
                li.attachments.length > 0 ? (
                    <AttachmentsDropdown
                        attachments={li.attachments}
                        activeAttachmentId={activeAttachmentId}
                        onSelectAttachment={(attachmentId: string) =>
                            onSelectAttachment(attachmentId, li.xeroBillInvoiceRequestId, li.friendlyName)
                        }
                    />
                ) : null,
        },
        {
            id: 'overflowMenu',
            name: '',
            getIsDisabled: (li) => billInvoiceRequestIdsWithError.includes(li.xeroBillInvoiceRequestId),
            value: (li) => li.friendlyName,
            cell: (value, li) => (
                <StyledActionMenu
                    panelFlow='center'
                    button={
                        <ActionButton>
                            <Flex alignItems='center'>
                                <TbEllipsisColorlessIcon />
                            </Flex>
                        </ActionButton>
                    }
                >
                    <ActionMenu.Item
                        disabled={removeLinesDisabled}
                        execute={() => removeSelectableBillById(li.xeroBillInvoiceRequestId)}
                        title={messages.menuItemRemove}
                    >
                        {messages.menuItemRemove}
                    </ActionMenu.Item>
                </StyledActionMenu>
            ),
        }
    );

    return columns;
};
